import { useId, type ReactNode } from "react";
import { classNames, getFeedbackSemantics, joinIds } from "./component-model";
import {
  getContentStatePresentation,
  type ContentStateKind,
} from "./content-state-model";

export interface ContentStateProps {
  readonly id?: string;
  readonly kind: ContentStateKind;
  readonly title: ReactNode;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
  readonly icon?: ReactNode;
  readonly label?: ReactNode;
  readonly headingLevel?: 2 | 3 | 4;
  readonly live?: boolean;
  readonly className?: string;
}

export function ContentState({
  id,
  kind,
  title,
  children,
  actions,
  icon,
  label,
  headingLevel = 2,
  live = true,
  className,
}: ContentStateProps) {
  const generatedId = useId();
  const stateId = id ?? `wh-content-state-${generatedId}`;
  const titleId = `${stateId}-title`;
  const bodyId = children ? `${stateId}-body` : undefined;
  const presentation = getContentStatePresentation(kind);
  const semantics = getFeedbackSemantics(presentation.tone, live);
  const Heading = `h${headingLevel}` as const;

  return (
    <section
      id={id}
      className={classNames(
        "wh-content-state",
        `wh-content-state--${kind}`,
        className,
      )}
      data-content-state={kind}
      aria-busy={presentation.busy ? "true" : undefined}
      aria-labelledby={titleId}
      aria-describedby={joinIds(bodyId) || undefined}
    >
      <span className="wh-content-state__indicator" aria-hidden="true">
        {icon ?? presentation.indicator}
      </span>
      <div className="wh-content-state__content">
        <div
          className="wh-content-state__message"
          role={semantics.role}
          aria-live={semantics.liveMode}
          aria-atomic={semantics.role ? "true" : undefined}
        >
          <p className="wh-content-state__label">
            {label ?? presentation.label}
          </p>
          <Heading className="wh-content-state__title" id={titleId}>
            {title}
          </Heading>
          {children && (
            <div className="wh-content-state__body" id={bodyId}>
              {children}
            </div>
          )}
        </div>
        {actions && <div className="wh-content-state__actions">{actions}</div>}
      </div>
    </section>
  );
}
